import { orderBy } from "lodash";

import { type SalesPageMetric } from "@north-beam/nb-client-side";

import { createGenericConversionRow } from "../../../../nb-api/src/endpoints/graphql/impl/overview-report-v2/conversion-row";

import { type MetricFilterOperator, type Sorting } from "./types";

export interface MetricRow {
  name: string;
  id: string;
  rev: number;
  rev_first_time: number;
  spend: number;
  txns: number;
  rev_returning: number;
  roasPercentage?: number;
  cacPercentage?: number;
  quantity: number;
  proportional_spend: number;
  order_rev: number;
}

function adjustValue(val: number) {
  return Math.abs(val) < 1e-6 ? 0 : val;
}

export function platformIdsForQuery(platforms: string[]): string[] {
  return platforms
    ?.map((selectedPlatform) => {
      const parts = selectedPlatform.split(";");
      parts.pop(); // Remove the last item
      return parts;
    })
    .flat();
}

export function filterDataByColumnAndOperation(
  value: number,
  array: any[],
  operation: MetricFilterOperator,
  column: string,
): any[] {
  switch (operation) {
    case "equalsTo":
      return array.filter((item) => item[column] === value);
    case "greaterThan":
      return array.filter((item) => item[column] > value);
    case "greaterThanEqualTo":
      return array.filter((item) => item[column] >= value);
    case "lessThan":
      return array.filter((item) => item[column] < value);
    case "lessThanOrEqualTo":
      return array.filter((item) => item[column] <= value);
    default:
      throw new Error("Invalid comparison operation");
  }
}

export function rollUpMetricRows({
  rows,
  attributionModel,
  attributionWindow,
  cacMetric,
  ROASMetric,
  AOVMetric,
  ASPMetric,
}: {
  rows: MetricRow[];
  attributionModel: string;
  attributionWindow: string;
  cacMetric: SalesPageMetric;
  ROASMetric: SalesPageMetric;
  AOVMetric: SalesPageMetric;
  ASPMetric: SalesPageMetric;
}) {
  return rows.map((row, index) => {
    const metrics = createGenericConversionRow(row);
    const pmetrics = createGenericConversionRow(row);
    const trueAOV = createGenericConversionRow(row);
    metrics.convs.push({
      am: attributionModel,
      aw: attributionWindow,
      txns: adjustValue(row.txns),
      rev: adjustValue(row.rev),
      ftRev: adjustValue(row.rev_first_time),
      rtnRev: adjustValue(row.rev_returning),
      spend: adjustValue(row.spend),
      quantity: row.quantity,
      orderRev: row.order_rev,
    } as never);
    pmetrics.convs.push({
      am: attributionModel,
      aw: attributionWindow,
      txns: adjustValue(row.txns),
      rev: adjustValue(row.rev),
      ftRev: adjustValue(row.rev_first_time),
      rtnRev: adjustValue(row.rev_returning),
      spend: adjustValue(row.proportional_spend),
      quantity: row.quantity,
      orderRev: row.order_rev,
    } as never);
    pmetrics.spend = row.proportional_spend;

    trueAOV.convs.push({
      am: attributionModel,
      aw: attributionWindow,
      txns: adjustValue(row.txns),
      rev: row.order_rev,
      ftRev: adjustValue(row.rev_first_time),
      rtnRev: adjustValue(row.rev_returning),
      spend: adjustValue(row.proportional_spend),
      quantity: row.quantity,
      orderRev: row.order_rev,
    } as never);

    return {
      ...row,
      index,
      itemSoldRatio: row.quantity / row.txns,
      name: row.name,
      id: row.id,
      metrics,
      aov: AOVMetric.calculate(trueAOV),
      roas: ROASMetric.calculate(metrics),
      cac: cacMetric.calculate(metrics),
      asp: ASPMetric.calculate(metrics),
      proas: ROASMetric.calculate(pmetrics),
      pcac: cacMetric.calculate(pmetrics),
    };
  });
}

export const assignPercentages = (data: MetricRow[]): MetricRow[] => {
  // Sort by roas and assign percentage
  const sortedByRoas = orderBy(data, ["proas"], ["asc"]);
  sortedByRoas.forEach((item, index) => {
    item.roasPercentage = (index / (sortedByRoas.length - 1)) * 100;
  });

  // Sort by cac and assign percentage
  const sortedByCac = orderBy(data, ["pcac"], ["desc"]);
  sortedByCac.forEach((item, index) => {
    item.cacPercentage = (index / (sortedByCac.length - 1)) * 100;
  });

  return data;
};

export const getBackgroundColorClass = (percentage: number) => {
  if (percentage > 75) {
    return "text-v2-action-positive bg-[rgba(80,186,88,0.10)]";
  }
  if (percentage > 33) {
    return "text-v2-primary  bg-v2-highlight";
  }
  return "text-v2-action-negative bg-[rgba(240,95,51,0.10)]";
};

export const quickSelectOptions = ({
  setSorting,
  setSelectedQuickSelect,
}: {
  setSorting(value: Sorting): void;
  setSelectedQuickSelect: (selectType: string) => void;
}) => {
  const options = [
    {
      id: "best_sellers",
      name: "Best Sellers",
      onClick: () => {
        setSorting({ id: "rev", order: "desc" });
        setSelectedQuickSelect("best_sellers");
      },
    },
    {
      id: "worst_sellers",
      name: "Worst Sellers",
      onClick: () => {
        setSorting({ id: "rev", order: "asc" });
        setSelectedQuickSelect("worst_sellers");
      },
    },
    {
      id: "lowest_marketing_cost",
      name: "Lowest Marketing Cost",
      onClick: () => {
        setSorting({ id: "cacPercentage", order: "desc" });
        setSelectedQuickSelect("lowest_marketing_cost");
      },
    },
    {
      id: "highest_marketing_cost",
      name: "Highest Marketing Cost",
      onClick: () => {
        setSorting({ id: "cacPercentage", order: "asc" });
        setSelectedQuickSelect("highest_marketing_cost");
      },
    },
    {
      id: "best_return_on_marketing",
      name: "Best Return on Marketing",
      onClick: () => {
        setSorting({ id: "roasPercentage", order: "desc" });
        setSelectedQuickSelect("best_return_on_marketing");
      },
    },
    {
      id: "worst_return_on_marketing",
      name: "Worst Return on Marketing",
      onClick: () => {
        setSorting({ id: "roasPercentage", order: "asc" });
        setSelectedQuickSelect("worst_return_on_marketing");
      },
    },
    {
      id: "bought_by_1st_time_customers",
      name: "Bought by 1st Time Customers",
      onClick: () => {
        setSorting({ id: "rev_first_time", order: "desc" });
        setSelectedQuickSelect("bought_by_1st_time_customers");
      },
    },
    // {
    //   id: "lead_to_a_2nd_purchase",
    //   name: "Lead to a 2nd Purchase",
    //   onClick: () => {
    //     setRowSort("rev_returning");
    //     setSortDirection("desc");
    //     setSelectedQuickSelect("lead_to_a_2nd_purchase");
    //   },
    // },
    {
      id: "highest_repurchase_rate",
      name: "Highest Repurchase Rate",
      onClick: () => {
        setSorting({ id: "rev_returning", order: "desc" });
        setSelectedQuickSelect("highest_repurchase_rate");
      },
    },
    // {
    //   id: "lowest_discount",
    //   name: "Lowest Discount",
    //   onClick: () => {
    //     setRowSort("avg_discount");
    //     setSortDirection("asc");
    //     setSelectedQuickSelect("lowest_discount");
    //   },
    // },
    // {
    //   id: "highest_discount",
    //   name: "Highest Discount",
    //   onClick: () => {
    //     setRowSort("avg_discount");
    //     setSortDirection("desc");
    //     setSelectedQuickSelect("highest_discount");
    //   },
    // },
  ];

  return options;
};
